// src/components/wrestler/PriceChart.jsx
import React from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  Label
} from 'recharts';
import CreditIcon from '../CreditIcon';


function PriceChart({
  data,
  compact = false,
  height = compact ? 130 : 180,
  showLabels = !compact,
  maxItems = compact ? 8 : 12
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartPalette = {
    surface: 'linear-gradient(180deg, rgba(18, 24, 31, 0.94), rgba(28, 38, 49, 0.94))',
    border: 'rgba(146, 201, 252, 0.16)',
    grid: 'rgba(146, 201, 252, 0.12)',
    text: '#ffffff',
    muted: '#abc0d7',
    tooltipBg: 'rgba(18, 24, 31, 0.96)',
    tooltipBorder: 'rgba(146, 201, 252, 0.22)',
    statBg: 'rgba(18, 24, 31, 0.88)',
    statBorder: 'rgba(32, 155, 1, 0.24)',
    areaMain: '#2db519',
    areaLight: 'rgba(45, 181, 25, 0.18)',
    accent: '#92c9fc',
  };

  // Trasforma i dati da Firestore
  const transformData = (firestoreData) => {
    if (!firestoreData || !Array.isArray(firestoreData)) return [];

    return firestoreData
      .filter(d => d && d.date && d.value !== undefined && d.value !== null)
      .map((item, index, array) => {
        let dateObj;

        // Gestione del timestamp di Firestore
        if (item.date && typeof item.date.toDate === 'function') {
          dateObj = item.date.toDate();
        } else if (item.date && item.date._seconds) {
          dateObj = new Date(item.date._seconds * 1000);
        } else if (item.date && typeof item.date === 'string') {
          dateObj = new Date(item.date);
        } else if (item.date && typeof item.date.getTime === 'function') {
          dateObj = item.date;
        } else {
          dateObj = new Date();
        }

        return {
          originalIndex: index,
          price: item.value,
          value: item.value,
          date: dateObj,
          dateStr: dateObj.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'short'
          }),
          formattedDate: dateObj.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }),
          isFirst: index === 0,
          isLast: index === array.length - 1,
          index: index
        };
      })
      .sort((a, b) => a.date - b.date);
  };

  // Prepara i dati
  const transformedData = transformData(data);
  const lastItems = transformedData.slice(-maxItems);

  // Formatta per recharts
  const chartData = lastItems.map((item, index) => ({
    ...item,
    index,
    xIndex: index
  }));

  if (!chartData.length) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: height,
        flexDirection: 'column',
        background: chartPalette.surface,
        borderRadius: 1,
        p: 2,
        border: `1px solid ${chartPalette.border}`
      }}>
        <CreditIcon className="me-1 fs-4 text-muted mb-2" size={16} />
        <Typography variant="body2" align="center" sx={{ color: chartPalette.muted }}>
          Dati insufficienti per il grafico
          <br />
          <small style={{ fontSize: '0.8rem' }}>
            {data?.length ? `${data.length} dati disponibili` : 'Nessun dato'}
          </small>
        </Typography>
      </Box>
    );
  }

  // Calcola statistiche
  const prices = chartData.map(d => d.price);
  const firstPrice = chartData[0]?.price || 0;
  const lastPrice = chartData[chartData.length - 1]?.price || 0;
  const difference = lastPrice - firstPrice;
  const percentageChange = firstPrice ? ((difference / firstPrice) * 100) : 0;
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const averagePrice = prices.reduce((a, b) => a + b, 0) / prices.length;

  // Formatta il prezzo
  const formatPrice = (value) => {
    if (!value && value !== 0) return '0';
    return `${Math.round(value)}`;
  };

  // Tooltip personalizzato
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const price = dataPoint.price;
      const dateStr = dataPoint.formattedDate;

      return (
        <Box sx={{
          bgcolor: chartPalette.tooltipBg,
          p: 1.5,
          borderRadius: 1,
          border: `1px solid ${chartPalette.tooltipBorder}`,
          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.28)',
          minWidth: 120
        }}>
          <Typography variant="caption" sx={{
            fontWeight: 600,
            color: '#9aef7b',
            display: 'block'
          }}>
            {formatPrice(price)}
          </Typography>
          <Typography variant="caption" sx={{
            color: chartPalette.muted,
            display: 'block'
          }}>
            {dateStr}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Formatta l'asse Y
  const formatYAxis = (value) => {
    return formatPrice(value);
  };

  // Formatta l'asse X
  const formatXAxis = (value) => {
    const dataPoint = chartData[value];
    if (!dataPoint) return '';

    if (value === 0 ||
      value === Math.floor(chartData.length / 2) ||
      value === chartData.length - 1) {
      return dataPoint.dateStr;
    }
    return '';
  };

  // Trova questa funzione nel PriceChart.jsx
  const getAreaColor = () => {
    return {
      main: chartPalette.areaMain,
      light: chartPalette.areaLight
    };
  };

  const areaColors = getAreaColor();

  return (
    <Box sx={{
      position: 'relative',
      height: height,
      background: chartPalette.surface,
      borderRadius: 1,
      p: compact ? 1 : 2,
      border: `1px solid ${chartPalette.border}`,
      overflow: 'hidden'
    }}>
      {/* Titolo e statistiche */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1
      }}>
        <Typography variant="caption" sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          fontSize: '0.8rem'
        }}>
        </Typography>

        {/* Statistiche */}
        {chartData.length >= 2 && (
          <Box sx={{
            position: 'absolute',
            bottom: compact ? 5 : 10,
            right: compact ? 5 : 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
            bgcolor: chartPalette.statBg,
            px: 1.5,
            py: 1,
            borderRadius: '6px',
            border: `1px solid ${chartPalette.statBorder}`,
            backdropFilter: 'blur(4px)',
            zIndex: 10
          }}>

            <Typography variant="caption" sx={{
              fontSize: '0.7rem',
              color: chartPalette.muted
            }}>
              Media: {formatPrice(averagePrice)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Grafico Recharts */}
      <Box sx={{
        height: `calc(100% - ${compact ? 30 : 40}px)`,
        width: '100%'
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{
              top: 5,
              right: 5,
              left: -20,
              bottom: showLabels ? 20 : 5
            }}
          >
            <CartesianGrid
              strokeDasharray="2 2"
              stroke={chartPalette.grid}
              horizontal={true}
              vertical={false}
            />

            <XAxis
              dataKey="index"
              tickFormatter={formatXAxis}
              tick={{
                fontSize: 10,
                fill: chartPalette.muted
              }}
              axisLine={false}
              tickLine={false}
              interval={0}
              minTickGap={1}
            />

            <YAxis
              tick={{
                fontSize: 10,
                fill: chartPalette.muted
              }}
              axisLine={false}
              tickLine={false}
              width={40}
              tickFormatter={formatYAxis}
              domain={['dataMin - 10%', 'dataMax + 10%']}
            />

            <Tooltip content={<CustomTooltip />} />

            {/* Area del grafico */}
            <Area
              type="monotone"
              dataKey="price"
              stroke={areaColors.main}
              strokeWidth={2}
              fill={areaColors.light}
              fillOpacity={0.6}
              strokeLinecap="round"
              dot={(props) => {
                const { index } = props;
                if (index % 2 === 0) {
                  return (
                    <circle
                      cx={props.cx}
                      cy={props.cy}
                      r={3}
                      fill={areaColors.main}
                      stroke="#12181f"
                      strokeWidth={1}
                    />
                  );
                }
                return null;
              }}
              activeDot={{
                r: 6,
                fill: areaColors.main,
                stroke: '#12181f',
                strokeWidth: 2
              }}
            />

            {/* Punto per l'ultimo prezzo - VERSIONE CORRETTA */}
            {chartData.length > 0 && (
              <ReferenceDot
                x={chartData[chartData.length - 1].index}
                y={chartData[chartData.length - 1].price}
                r={6}
                fill={areaColors.main}
                stroke="#12181f"
                strokeWidth={2}
              >
                <Label
                  position="top"
                  offset={10}
                  fill={chartPalette.accent}
                  fontSize={10}
                  fontWeight="bold"
                >
                  {formatPrice(chartData[chartData.length - 1].price)}
                </Label>
              </ReferenceDot>
            )}

            {/* Linea orizzontale per il prezzo iniziale */}
            {firstPrice > 0 && (
              <ReferenceDot
                x={0}
                y={firstPrice}
                r={0}
                fill="transparent"
                stroke={chartPalette.grid}
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      {/* Range dei prezzi in basso a sinistra */}
      <Box sx={{
        position: 'absolute',
        bottom: compact ? 2 : 5,
        left: compact ? 5 : 10,
        display: 'flex',
        gap: 1,
        alignItems: 'center'
      }}>
        {/*
        <Typography variant="caption" sx={{
          fontSize: '0.7rem',
          color: theme.palette.text.secondary,
          fontWeight: 500
        }}>
          Min: {formatPrice(minPrice)}
        </Typography>
        <span style={{ color: theme.palette.divider }}>|</span>
        <Typography variant="caption" sx={{
          fontSize: '0.7rem',
          color: theme.palette.text.secondary,
          fontWeight: 500
        }}>
          Max: {formatPrice(maxPrice)}
        </Typography>
          */}
      </Box>
    </Box>
  );
}

export default PriceChart;
